import { ColDef, ColGroupDef, GridOptions } from 'ag-grid-community'
import { ProductSectorDetail } from 'api'
import {
  createActionsRenderer,
  createGroupedActionRenderer,
  gridOptionsBase,
  StatusCellRenderer,
  TypedValueGetterParams,
  withAllContext
} from 'view/components/datagrid'
import { Locale, Translation } from 'locale'
import { SectorActionsButton } from './SectorActionsButton'
import { listFormatter } from 'view/shared/datagrid'
import { CollateralBasketTypeHelper, ExDividendDayConventionHelper } from 'model'
import { getSectorStatusColor } from '../utils'

export enum SectorGridColumnId {
  SECTOR_ACTIONS = 'SECTOR_ACTIONS',
  SECTOR_STATUS = 'SECTOR_STATUS',
  SECTOR_NAME = 'SECTOR_NAME',
  SECTOR_PRODUCT_NAME = 'SECTOR_PRODUCT_NAME',
  SECTOR_DEFAULT_NOTICE_PERIOD = 'SECTOR_DEFAULT_NOTICE_PERIOD',
  SECTOR_DEFAULT_NOTICE_TIME = 'SECTOR_DEFAULT_NOTICE_TIME',
  SECTOR_DEFAULT_REPO_TYPE = 'SECTOR_DEFAULT_REPO_TYPE',
  SECTOR_SUPPORTED_REPO_TYPES = 'SECTOR_SUPPORTED_REPO_TYPES',
  SECTOR_REFERENCE_RATES = 'SECTOR_REFERENCE_RATES',
  SECTOR_EX_DIVIDEND_DAY_CONVENTION = 'SECTOR_EX_DIVIDEND_DAY_CONVENTION',
  SECTOR_EX_DIVIDEND_DAYS = 'SECTOR_EX_DIVIDEND_DAYS',
  SECTOR_BASIS_CONVENTION = 'SECTOR_BASIS_CONVENTION',
  SECTOR_SETTLEMENT_VENUE = 'SECTOR_SETTLEMENT_VENUE',
  SECTOR_BASKET_TYPE = 'SECTOR_BASKET_TYPE',
  SECTOR_BASKET_CURRENCY = 'SECTOR_BASKET_CURRENCY',
  SECTOR_START_CONSIDERATION_OVERRIDE_THRESHOLD = 'SECTOR_START_CONSIDERATION_OVERRIDE_THRESHOLD'
}

export const SectorColumnDefs: (ColGroupDef | ColDef)[] = [
  {
    colId: SectorGridColumnId.SECTOR_ACTIONS,
    headerName: Locale.translate(Translation.TC_EMPTY_HEADER),
    equals: () => false,
    width: 180,
    minWidth: 180,
    headerComponent: withAllContext(createGroupedActionRenderer(SectorActionsButton)),
    cellClass: 'action-cell',
    cellRenderer: withAllContext(createActionsRenderer(SectorActionsButton)),
    pinned: 'left',
    lockPinned: true,
    enableRowGroup: false,
    lockVisible: true,
    filter: false,
    sortable: false
  },
  {
    colId: SectorGridColumnId.SECTOR_STATUS,
    headerName: Locale.translate(Translation.TC_STATUS),
    equals: () => false,
    width: 180,
    minWidth: 180,
    valueGetter: (params: TypedValueGetterParams<ProductSectorDetail>) => params.data?.productSector.status,
    cellRenderer: StatusCellRenderer,
    cellRendererParams: {
      getStatusColor: (data: ProductSectorDetail) => getSectorStatusColor(data?.productSector.status)
    }
  },
  {
    colId: SectorGridColumnId.SECTOR_NAME,
    valueGetter: (params: TypedValueGetterParams<ProductSectorDetail>) => params.data?.productSector.name,
    headerName: Locale.translate(Translation.TC_SECTOR),
    width: 180,
    filter: 'agSetColumnFilter'
  },
  {
    colId: SectorGridColumnId.SECTOR_PRODUCT_NAME,
    valueGetter: (params: TypedValueGetterParams<ProductSectorDetail>) => params.data?.product?.name,
    headerName: Locale.translate(Translation.TC_PRODUCT),
    width: 100,
    filter: 'agSetColumnFilter'
  },
  {
    colId: SectorGridColumnId.SECTOR_DEFAULT_NOTICE_PERIOD,
    valueGetter: (params: TypedValueGetterParams<ProductSectorDetail>) => params.data?.productSector.defaultNoticePeriod,
    headerName: Locale.translate(Translation.TC_DEFAULT_NOTICE_PERIOD),
    width: 180,
    filter: 'agSetColumnFilter'
  },
  {
    colId: SectorGridColumnId.SECTOR_DEFAULT_NOTICE_TIME,
    valueGetter: (params: TypedValueGetterParams<ProductSectorDetail>) => params.data?.productSector.defaultNotificationDeadlineTime,
    headerName: Locale.translate(Translation.TC_DEFAULT_NOTICE_TIME),
    width: 150,
    filter: 'agSetColumnFilter'
  },
  {
    colId: SectorGridColumnId.SECTOR_DEFAULT_REPO_TYPE,
    valueGetter: (params: TypedValueGetterParams<ProductSectorDetail>) => params.data?.productSector.defaultRepoType,
    headerName: Locale.translate(Translation.TC_DEFAULT_REPO_TYPE),
    width: 150,
    filter: 'agSetColumnFilter'
  },
  {
    colId: SectorGridColumnId.SECTOR_SUPPORTED_REPO_TYPES,
    valueGetter: (params: TypedValueGetterParams<ProductSectorDetail>) => params.data?.productSector.supportedRepoTypes,
    valueFormatter: listFormatter,
    headerName: Locale.translate(Translation.TC_SUPPORT_REPO_TYPES),
    width: 170,
    filter: 'agSetColumnFilter'
  },
  {
    colId: SectorGridColumnId.SECTOR_REFERENCE_RATES,
    valueGetter: (params: TypedValueGetterParams<ProductSectorDetail>) =>
      params.data?.referenceRates?.map(referenceRate => referenceRate.name),
    valueFormatter: listFormatter,
    headerName: Locale.translate(Translation.TC_REFERENCE_RATES),
    width: 150,
    filter: 'agSetColumnFilter'
  },
  {
    colId: SectorGridColumnId.SECTOR_EX_DIVIDEND_DAY_CONVENTION,
    valueGetter: (params: TypedValueGetterParams<ProductSectorDetail>) => {
      const value = params.data?.productSector.exDividendDayConvention
      return value && ExDividendDayConventionHelper.getName(value)
    },
    headerName: Locale.translate(Translation.TC_EX_DIVIDEND_DAY_CONVENTION),
    width: 180,
    filter: 'agSetColumnFilter'
  },
  {
    colId: SectorGridColumnId.SECTOR_EX_DIVIDEND_DAYS,
    valueGetter: (params: TypedValueGetterParams<ProductSectorDetail>) => params.data?.productSector.exDividendDays,
    headerName: Locale.translate(Translation.TC_EX_DIVIDEND_DAYS),
    width: 120,
    filter: 'agSetColumnFilter'
  },
  {
    colId: SectorGridColumnId.SECTOR_BASIS_CONVENTION,
    valueGetter: (params: TypedValueGetterParams<ProductSectorDetail>) => params.data?.productSector.basisConvention,
    headerName: Locale.translate(Translation.TC_BASIS_CONVENTION),
    width: 120,
    filter: 'agSetColumnFilter'
  },
  {
    colId: SectorGridColumnId.SECTOR_BASKET_TYPE,
    valueGetter: (params: TypedValueGetterParams<ProductSectorDetail>) => {
      const basketType = params.data?.productSector.basketType
      return basketType && CollateralBasketTypeHelper.getName(basketType)
    },
    headerName: Locale.translate(Translation.TC_BASKET_TYPE),
    width: 120,
    filter: 'agSetColumnFilter'
  },
  {
    colId: SectorGridColumnId.SECTOR_BASKET_CURRENCY,
    valueGetter: (params: TypedValueGetterParams<ProductSectorDetail>) => params.data?.productSector.basketCurrency,
    headerName: Locale.translate(Translation.TC_BASKET_CURRENCY),
    width: 120,
    filter: 'agSetColumnFilter'
  },
  {
    colId: SectorGridColumnId.SECTOR_START_CONSIDERATION_OVERRIDE_THRESHOLD,
    valueGetter: (params: TypedValueGetterParams<ProductSectorDetail>) => params.data?.productSector.startConsiderationOverrideThreshold,
    headerName: Locale.translate(Translation.TC_START_CONSIDERATION_OVERRIDE_THRESHOLD),
    width: 120,
    filter: 'agSetColumnFilter'
  }
]

export const sectorGridOptions: GridOptions = {
  ...gridOptionsBase,
  getRowId: ({ data }) => data.productSector.id
}
